import { useQueryClient } from '@tanstack/react-query';
import { ColorNeutralGrey3 } from 'autogen/design-tokens/tokens';
import type { ChoosablePrizeCertificateModel } from 'autogen/swagger/all';
import {
    PrizeCertificatesQueryKey,
    CheckoutDeliveryBasketQueryKey,
    useAddPrizeCertificateMutation
} from 'autogen/swagger/all/hooks';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { Button } from 'components/Button';
import { updateBasket } from 'components/basket/shared/basket';
import { Typography } from 'components/generic/typography';
import { useTranslation } from 'react-i18next';
import toast from 'components/Toast';
import styled from 'styled-components';

const StyledButton = styled(Button)`
    flex-direction: column;
    border: 1px solid ${ColorNeutralGrey3};
`;

export default function ChoosablePrizeCertificatesRenderer({
    model: choosablePrizeCertificates,
    onCertificateSuccessfullyAdded
}: {
    model: ChoosablePrizeCertificateModel[];
    onCertificateSuccessfullyAdded?: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    /**
     * Important note about adding/removing prizeCerts in checkout
     * All calls to add / remove has to be made to a checkout controller otherwise the active basket will switch.
     * This can cause issues if you're currently purchasing a booking ticket and decide to add a prizeCert, your active basket can become the one with physical products.
     */
    const { mutate } = useAddPrizeCertificateMutation({
        onError: () => toast.error(t(GeneralExceptionError)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [PrizeCertificatesQueryKey] });
            queryClient.invalidateQueries({ queryKey: [CheckoutDeliveryBasketQueryKey] });
            updateBasket();

            if (onCertificateSuccessfullyAdded) {
                onCertificateSuccessfullyAdded();
            }
        }
    });

    return (
        <>
            {choosablePrizeCertificates.map((choosablePrizeCertificate) => {
                const { isActive, prizeCertificate } = choosablePrizeCertificate;

                if (!isActive) return null;

                return (
                    <StyledButton
                        key={prizeCertificate.id}
                        type="button"
                        variant="ghostOnLight"
                        size="lg"
                        onClick={() =>
                            mutate({
                                params: { prizeId: prizeCertificate.id }
                            })
                        }
                    >
                        <Typography.Body as="span" size="L">
                            {prizeCertificate.valueText}
                        </Typography.Body>

                        <Typography.Body as="span" size="S">
                            {prizeCertificate.description}
                        </Typography.Body>
                    </StyledButton>
                );
            })}
        </>
    );
}
